import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthService } from 'app/core/auth/auth.service';
import { composeUrl } from 'app/core/shared/helper/url.helper';
import { environment } from 'environments/environment';
import { BehaviorSubject, Observable, tap } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class WellHeaderInfoService {
    private _assetsTree: BehaviorSubject<any> = new BehaviorSubject(null);

    private _wellsTree: BehaviorSubject<any> = new BehaviorSubject(null);
    private _wellsFilter: BehaviorSubject<any> = new BehaviorSubject(null);
    private _selectedWellPlatform: BehaviorSubject<any> = new BehaviorSubject(
        null
    );

    private _companies: BehaviorSubject<any> = new BehaviorSubject(null);
    private _companyDetails: BehaviorSubject<any> = new BehaviorSubject(null);

    private _overallOverviewDetails: BehaviorSubject<any> = new BehaviorSubject(
        null
    );

    private _parentsNode: BehaviorSubject<any> = new BehaviorSubject(null);

    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient,
        private authService: AuthService
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for data
     */

    get assetsTree$(): Observable<any> {
        return this._assetsTree.asObservable();
    }

    get wellsTree$(): Observable<any> {
        return this._wellsTree.asObservable();
    }

    get wellsFilter$(): Observable<any> {
        return this._wellsFilter.asObservable();
    }

    get companies$(): Observable<any> {
        return this._companies.asObservable();
    }

    get companyDetails$(): Observable<any> {
        return this._companyDetails.asObservable();
    }

    get overallOverviewDetails$(): Observable<any> {
        return this._overallOverviewDetails.asObservable();
    }

    get parentsNode$(): Observable<any> {
        return this._parentsNode.asObservable();
    }

    get selectedWellPlatform$(): Observable<any> {
        return this._selectedWellPlatform.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * ASSET TREE
     */
    getAssetTree(): Observable<any> {
        return this._httpClient
            .get<any>(
                `${environment.apiURL}/${environment.apiVersion}/WellHeaderInformation/assets`
            )
            .pipe(
                tap((response: any) => {
                    this._assetsTree.next(response.data);
                })
            );
    }

    updateParentsNode(node: any): void {
        this._parentsNode.next(node);
    }

    /**
     * WELLS TREE
     */

    getWellFilter(selectedFieldId: any): Observable<any> {
        let Params = new HttpParams();
        Params = Params.append('fieldId', selectedFieldId);

        return this._httpClient
            .get<any>(
                `${environment.apiURL}/${environment.apiVersion}/WellHeaderInformation/well-info-tree/filters`,
                {
                    params: Params,
                }
            )
            .pipe(
                tap((response: any) => {
                    this._wellsFilter.next(response);
                })
            );
    }

    getWellsTree(platformId: any): Observable<any> {
        return this._httpClient
            .get<any>(
                `${environment.apiURL}/${environment.apiVersion}/WellHeaderInformation/well-info-tree/${platformId}`
            )
            .pipe(
                tap((response: any) => {
                    this._wellsTree.next(response.data);
                })
            );
    }

    resetWellsTree() {
        this._wellsTree.next(null); // or any initial/default state
    }

    updatedSelectedWellPlatform(selectedPlatform: any): void {
        this._selectedWellPlatform.next(selectedPlatform);
    }

    /**
     * COMPANY
     */
    getCompanyById(id: any): Observable<any> {
        return this._httpClient
            .get<any>(
                `${environment.apiURL}/${environment.apiVersion}/WellHeaderInformation/Operator/GetCompany/${id}`
            )
            .pipe(
                tap((response: any) => {
                    this._companyDetails.next(response);
                })
            );
    }

    createCompany(payload: any): Observable<any> {
        return this._httpClient
            .post<any>(
                `${environment.apiURL}/${environment.apiVersion}/WellHeaderInformation/Operator/CreateOperator`,
                payload
            )
            .pipe(
                tap((response: any) => {
                    return response;
                })
            );
    }

    updateCompany(payload: any): Observable<any> {
        return this._httpClient
            .put<any>(
                `${environment.apiURL}/${environment.apiVersion}/WellHeaderInformation/Operator/UpdateOperator`,
                payload
            )
            .pipe(
                tap((response: any) => {
                    return response;
                })
            );
    }

    deleteCompany(id: any): Observable<any> {
        return this._httpClient
            .delete<any>(
                `${environment.apiURL}/${environment.apiVersion}/WellHeaderInformation/Operator/DeleteOperator/${id}`
            )
            .pipe(
                tap((response: any) => {
                    return response;
                })
            );
    }

    /**
     * Petroleum Sharing Contract
     */
    getPscById(id: any): Observable<any> {
        return this._httpClient
            .get<any>(
                `${environment.apiURL}/${environment.apiVersion}/WellHeaderInformation/psc/GetPetroleumSharingContract/${id}`
            )
            .pipe(tap((response: any) => {}));
    }

    createPsc(payload: any): Observable<any> {
        return this._httpClient
            .post<any>(
                `${environment.apiURL}/${environment.apiVersion}/WellHeaderInformation/psc/CreatePetroleumSharingContract`,
                payload
            )
            .pipe(
                tap((response: any) => {
                    return response;
                })
            );
    }

    updatePsc(payload: any): Observable<any> {
        return this._httpClient
            .put<any>(
                `${environment.apiURL}/${environment.apiVersion}/WellHeaderInformation/psc/UpdatePetroleumSharingContract`,
                payload
            )
            .pipe(
                tap((response: any) => {
                    return response;
                })
            );
    }

    deletePsc(id: any): Observable<any> {
        return this._httpClient
            .delete<any>(
                `${environment.apiURL}/${environment.apiVersion}/WellHeaderInformation/psc/DeletePetroleumSharingContract/${id}`
            )
            .pipe(
                tap((response: any) => {
                    return response;
                })
            );
    }

    /**
     * Fields
     */
    getFieldById(id: any): Observable<any> {
        return this._httpClient
            .get<any>(
                `${environment.apiURL}/${environment.apiVersion}/WellHeaderInformation/Fields/GetField/${id}`
            )
            .pipe(tap((response: any) => {}));
    }

    createFields(payload: any): Observable<any> {
        return this._httpClient
            .post<any>(
                `${environment.apiURL}/${environment.apiVersion}/WellHeaderInformation/Fields/CreateField`,
                payload
            )
            .pipe(
                tap((response: any) => {
                    return response;
                })
            );
    }

    updateFields(payload: any): Observable<any> {
        return this._httpClient
            .put<any>(
                `${environment.apiURL}/${environment.apiVersion}/WellHeaderInformation/Fields/UpdateField`,
                payload
            )
            .pipe(
                tap((response: any) => {
                    return response;
                })
            );
    }

    deleteFields(id: any): Observable<any> {
        return this._httpClient
            .delete<any>(
                `${environment.apiURL}/${environment.apiVersion}/WellHeaderInformation/Fields/DeleteField/${id}`
            )
            .pipe(
                tap((response: any) => {
                    return response;
                })
            );
    }

    /**
     * Platform
     */
    getPlatformById(id: any): Observable<any> {
        return this._httpClient
            .get<any>(
                `${environment.apiURL}/${environment.apiVersion}/WellHeaderInformation/Platforms/GetPlatform/${id}`
            )
            .pipe(tap((response: any) => {}));
    }

    createPlatform(payload: any): Observable<any> {
        return this._httpClient
            .post<any>(
                `${environment.apiURL}/${environment.apiVersion}/WellHeaderInformation/Platforms/CreatePlatform`,
                payload
            )
            .pipe(
                tap((response: any) => {
                    return response;
                })
            );
    }

    updatePlatform(payload: any): Observable<any> {
        return this._httpClient
            .put<any>(
                `${environment.apiURL}/${environment.apiVersion}/WellHeaderInformation/Platforms/UpdatePlatform`,
                payload
            )
            .pipe(
                tap((response: any) => {
                    return response;
                })
            );
    }

    deletePlatform(id: any): Observable<any> {
        return this._httpClient
            .delete<any>(
                `${environment.apiURL}/${environment.apiVersion}/WellHeaderInformation/Platforms/DeletePlatform/${id}`
            )
            .pipe(
                tap((response: any) => {
                    return response;
                })
            );
    }

    /**
     * Wells
     */
    getWellsById(id: any): Observable<any> {
        return this._httpClient
            .get<any>(
                `${environment.apiURL}/${environment.apiVersion}/WellHeaderInformation/Wells/GetWell/${id}`
            )
            .pipe(tap((response: any) => {}));
    }

    createWells(payload: any): Observable<any> {
        return this._httpClient
            .post<any>(
                `${environment.apiURL}/${environment.apiVersion}/WellHeaderInformation/Wells/CreateWell`,
                payload
            )
            .pipe(
                tap((response: any) => {
                    return response;
                })
            );
    }

    updateWells(payload: any): Observable<any> {
        return this._httpClient
            .put<any>(
                `${environment.apiURL}/${environment.apiVersion}/WellHeaderInformation/Wells/UpdateWell`,
                payload
            )
            .pipe(
                tap((response: any) => {
                    return response;
                })
            );
    }

    deleteWell(id: any): Observable<any> {
        return this._httpClient
            .delete<any>(
                `${environment.apiURL}/${environment.apiVersion}/WellHeaderInformation/Wells/DeleteWell/${id}`
            )
            .pipe(
                tap((response: any) => {
                    return response;
                })
            );
    }

    /**
     * Wellbore
     */
    getWellbore(id: any): Observable<any> {
        return this._httpClient
            .get<any>(
                `${environment.apiURL}/${environment.apiVersion}/WellHeaderInformation/Wellbore/GetWellbore/${id}`
            )
            .pipe(tap((response: any) => {}));
    }

    createWellbore(payload: any): Observable<any> {
        return this._httpClient
            .post<any>(
                `${environment.apiURL}/${environment.apiVersion}/WellHeaderInformation/Wellbore/CreateWellbore`,
                payload
            )
            .pipe(
                tap((response: any) => {
                    return response;
                })
            );
    }

    updateWellbore(payload: any): Observable<any> {
        return this._httpClient
            .put<any>(
                `${environment.apiURL}/${environment.apiVersion}/WellHeaderInformation/Wellbore/UpdateWellbore`,
                payload
            )
            .pipe(
                tap((response: any) => {
                    return response;
                })
            );
    }

    deleteWellbore(id: any): Observable<any> {
        return this._httpClient
            .delete<any>(
                `${environment.apiURL}/${environment.apiVersion}/WellHeaderInformation/Wellbore/DeleteWellbore/${id}`
            )
            .pipe(
                tap((response: any) => {
                    return response;
                })
            );
    }

    /**
     * WellString
     */
    getWellString(id: any): Observable<any> {
        return this._httpClient
            .get<any>(
                `${environment.apiURL}/${environment.apiVersion}/WellHeaderInformation/Strings/GetWellString/${id}`
            )
            .pipe(tap((response: any) => {}));
    }

    createWellString(payload: any): Observable<any> {
        return this._httpClient
            .post<any>(
                `${environment.apiURL}/${environment.apiVersion}/WellHeaderInformation/Strings/CreateWellString`,
                payload
            )
            .pipe(
                tap((response: any) => {
                    return response;
                })
            );
    }

    updateWellString(payload: any): Observable<any> {
        return this._httpClient
            .put<any>(
                `${environment.apiURL}/${environment.apiVersion}/WellHeaderInformation/Strings/UpdateWellString`,
                payload
            )
            .pipe(
                tap((response: any) => {
                    return response;
                })
            );
    }

    deleteWellString(id: any): Observable<any> {
        return this._httpClient
            .delete<any>(
                `${environment.apiURL}/${environment.apiVersion}/WellHeaderInformation/Strings/DeleteWellString/${id}`
            )
            .pipe(
                tap((response: any) => {
                    return response;
                })
            );
    }

    /**
     * Overall Overview
     */

    getOverallOverview(parameter: any): Observable<any> {
        if (parameter.Field == 'all') {
            parameter.Field = '';
        }

        if (parameter.Platform == 'all') {
            parameter.Platform = '';
        }

        const url = composeUrl(
            `/${environment.apiVersion}/WellHeaderInformation/Overview/GetOverallOverview`,
            parameter
        );

        return this._httpClient.get<any>(url).pipe(
            tap((response: any) => {
                this._overallOverviewDetails.next(response);
            })
        );
    }

    downloadOverallOverview(field: any, platform: any): Observable<any> {
        let Params = new HttpParams();
        Params = Params.append('field', field);
        Params = Params.append('platform', platform);

        return this._httpClient
            .get(
                `${environment.apiURL}/${environment.apiVersion}/WellHeaderInformation/Overview/DownloadOverview`,
                {
                    params: Params,
                    responseType: 'blob',
                }
            )
            .pipe(
                tap((response: any) => {
                    return response;
                })
            );
    }
}
